import merge from "ts-deepmerge";

import {
  EmployerPermissions,
  EngagePermissions as NativeEngagePermissions,
  TrackyrPermissions,
} from "./queries";
import type {
  Access,
  ShortlysterPermissions as ShortlysterPermissions,
} from "./types";

export type EngagePermissions = {
  insights?: {
    view?: boolean;
    edit?: boolean;
  };
  campaigns?: {
    view?: boolean;
    edit?: boolean;
  };
  shortlyster?: {
    users?: boolean;
    account?: boolean;
    settings?: boolean;
  };
};

type Simplify<T> = {
  [K in keyof T]: Simplify<T[K]> extends infer O
    ? { [K in keyof O]: O[K] }
    : never;
};

export type Permissions = Simplify<
  ShortlysterPermissions &
    TrackyrPermissions &
    EmployerPermissions &
    EngagePermissions & {
      product?: {
        engage?: boolean;
        engageAdmin?: boolean;
        hire?: boolean;
        develop?: boolean;
        developAdmin?: boolean;
        insights?: boolean;
      };
      compono?: {
        admin?: boolean;
      };
    }
>;

const transformEngagePermissions = (perms?: NativeEngagePermissions) => {
  if (!perms) return {};

  const engagePerms: EngagePermissions = {};

  perms.permissions.forEach((p) => {
    const [feat, perm] = p.split(".");
    if (!engagePerms[feat as keyof EngagePermissions]) {
      engagePerms[feat as keyof EngagePermissions] = {};
    }
    (engagePerms[feat as keyof EngagePermissions] as any)[perm] = true;
  });
  return engagePerms;
};

export const resolvePermissions = (
  roles: Access["allAppsRoles"],
  sl: ShortlysterPermissions,
  tr: TrackyrPermissions,
  em: EmployerPermissions,
  en?: NativeEngagePermissions,
): Permissions => {
  // Job Requisition capabilities are role-derived (the permissions query does
  // not return them). Mirrors the listings subgraph's capability matrix
  // (services/listings requisitionPermissions.ts): configure is Owner/Admin
  // only; the tab (TP-1598) gates on `jobRequisitions.configure`.
  const hireRoles =
    roles?.find((r) => r.appCode === "shortlyster")?.roles ?? [];
  const hasHireRole = (...allowed: string[]) =>
    hireRoles.some((role) => allowed.includes(role));
  // Spec §9 matrix. Requisition-page access (view/manage) covers
  // Owner/Admin/Recruitment Manager (`user`; `editor` is also granted it, but
  // maps to no product role — see requisitionPermissions.ts) and Hiring Managers
  // (hiringManager) — team scoping ("same team") is enforced server-side, so
  // these flags only gate navigation/routes. Creating a Job from an approved
  // requisition is NOT covered here — it is a server-resolved per-record
  // permission (`createJob`), which excludes Hiring Managers. Reviewers have
  // none. Any Hire role can be a named
  // approver, so the Approvals inbox (`viewApprovals`) is open to all;
  // `viewAllApprovals` remains Owner/Admin (§5.3).
  const jobRequisitions = {
    view: hasHireRole("owner", "admin", "editor", "user", "hiringManager"),
    create: hasHireRole("owner", "admin", "editor", "user", "hiringManager"),
    manage: hasHireRole("owner", "admin", "editor", "user", "hiringManager"),
    configure: hasHireRole("owner", "admin"),
    viewApprovals: hasHireRole(
      "owner",
      "admin",
      "editor",
      "user",
      "hiringManager",
      "viewer",
    ),
    viewAllApprovals: hasHireRole("owner", "admin"),
  };

  return merge(
    sl,
    tr ?? {},
    em,
    transformEngagePermissions(en),
    { jobRequisitions },
    {
      product: {
        // the following /should/ work, but doesn't if the user has only engage access
        // engage: Boolean(roles?.find((r) => r.appCode === "retayner")),
        engage: !!en,
        engageAdmin: Boolean(
          roles?.find(
            (r) =>
              r.appCode === "retayner" &&
              r.roles?.some((role) => role === "owner" || role === "admin"),
          ),
        ),
        hire: Boolean(roles?.find((r) => r.appCode === "shortlyster")),
        develop: Boolean(roles?.find((r) => r.appCode === "develop")),
        developAdmin: Boolean(
          roles?.find(
            (r) =>
              r.appCode === "develop" &&
              r.roles?.some((role) => role === "owner" || role === "admin"),
          ),
        ),
      },
    },
    { compono: { admin: !!sl.isComponoAdmin } },
  );
};

const mergeDevelopPermissions = (
  obj1: Record<string, boolean>,
  obj2: Record<string, boolean>,
): Record<string, boolean> => {
  const result: Record<string, boolean> = { ...obj1 };

  for (const key in obj2) {
    if (Object.prototype.hasOwnProperty.call(obj2, key)) {
      result[key] = result[key] || obj2[key];
    }
  }

  return result;
};

export const resolveDevelopPermissions = (
  developData?: { permissions: Record<string, boolean> },
  groupsData?: any,
) => {
  const developPermissions = developData?.permissions || {
    learningPortal: false,
  };
  const groupPermissions = {
    learningPortal: groupsData?.myGroupMembers.some(
      (member) => member.role !== "learner",
    ),
  };
  return mergeDevelopPermissions(developPermissions, groupPermissions);
};
